import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AgentStatus, agentStatus, AgentStatusBadge, agentStatusBadge, AgentV3 } from './frontdoor-v3-agent.service';

/** The two roles an agent can host a share in. */
export type ShareHostRole = 'active' | 'passive';

/** The host lists every kind of share carries: TCP and IPsec alike. */
export interface ShareHosts {
    activeAgentIds?: string[];
    passiveAgentIds?: string[];
}

/** The path the service reports each role's faults under, on every kind of share. */
export const SHARE_HOST_PATHS: Record<ShareHostRole, string> = {
    active: '$.activeAgentIds',
    passive: '$.passiveAgentIds',
};

/** One agent hosting a share, resolved from the agents a page already reads. */
export interface ShareHostChip {
    id: string;
    /** The agent's name, or its id where the agent could not be read. */
    label: string;
    role: ShareHostRole;
    status: AgentStatus;
    badge: AgentStatusBadge;
    /** False where the agent read did not return this agent, such as one deleted since. */
    known: boolean;
}

/** What a listing's Hosting cell draws for one share. */
export interface HostingCell {
    hosts: ShareHostChip[];
    /** The first host's name, or '' for a share naming none. */
    primary: string;
    /** The other hosts, counted by role, such as "+1 active, +2 passive". */
    extra: string;
    /** One line per host, for the tooltip. */
    tooltip: string;
}

/** Every agent that hosts `share`: the actives first, then the passives. */
export function shareAgentIds(share: ShareHosts | undefined): string[] {
    return [...(share?.activeAgentIds ?? []), ...(share?.passiveAgentIds ?? [])];
}

/**
 * Every host of `share` with its role and agent status, the actives first. The status is the agent's own, not whether
 * it serves this share, because a listing row carries no `hosting` block.
 */
export function shareHostChips(share: ShareHosts | undefined, agentsById: Map<string, AgentV3>): ShareHostChip[] {
    const chip = (id: string, role: ShareHostRole): ShareHostChip => {
        const agent = agentsById.get(id);
        const status = agent ? agentStatus(agent) : 'unknown';
        return { id, label: agent?.name || id, role, status, badge: agentStatusBadge(status), known: !!agent };
    };
    return [
        ...(share?.activeAgentIds ?? []).map((id) => chip(id, 'active')),
        ...(share?.passiveAgentIds ?? []).map((id) => chip(id, 'passive')),
    ];
}

/** The hosts of a share, counted by role and by those online. */
export interface ShareHostCounts {
    total: number;
    active: number;
    passive: number;
    online: number;
}

export function shareHostCounts(hosts: ShareHostChip[]): ShareHostCounts {
    return {
        total: hosts.length,
        active: hosts.filter((host) => host.role === 'active').length,
        passive: hosts.filter((host) => host.role === 'passive').length,
        online: hosts.filter((host) => host.status === 'online').length,
    };
}

/** The Agents readout's foot line: "2 active · 1 passive · 2 online". */
export function hostsFoot(counts: ShareHostCounts): string {
    return `${counts.active} active · ${counts.passive} passive · ${counts.online} online`;
}

/** The hosts of one share, and the agents they were resolved from, to keep for the next read. */
export interface ShareHostsRead {
    hosts: ShareHostChip[];
    agentsById: Map<string, AgentV3>;
}

/**
 * The hosts of `share` once `agents$` answers. A failed read never errors: it resolves the hosts against `lastKnown`,
 * so a page keeps the last statuses it saw, or lists the hosts by id when it saw none.
 */
export function resolveShareHosts(
    share: ShareHosts,
    agents$: Observable<AgentV3[]>,
    lastKnown: Map<string, AgentV3>
): Observable<ShareHostsRead> {
    return agents$.pipe(
        map((agents) => new Map(agents.map((agent) => [agent.id, agent]))),
        catchError(() => of(lastKnown)),
        map((agentsById) => ({ hosts: shareHostChips(share, agentsById), agentsById }))
    );
}

/** `address:port`, or the address alone without a port, or '' where the share names no target. */
export function shareTarget(share: { targetAddress?: string; targetPort?: number } | null | undefined): string {
    const address = share?.targetAddress ?? '';
    const port = share?.targetPort;
    return address && port ? `${address}:${port}` : address;
}

/** What the Hosting cell draws for `share`: the first host by name, the rest counted by role. */
export function shareHostingCell(share: ShareHosts | undefined, agentsById: Map<string, AgentV3>): HostingCell {
    const hosts = shareHostChips(share, agentsById);
    const others = hosts.slice(1);
    const extra = (['active', 'passive'] as ShareHostRole[])
        .map((role) => ({ role, count: others.filter((host) => host.role === role).length }))
        .filter(({ count }) => count > 0)
        .map(({ role, count }) => `+${count} ${role}`)
        .join(', ');
    return {
        hosts,
        primary: hosts[0]?.label ?? '',
        extra,
        tooltip: hosts
            .map((host) => `${host.label}: ${host.role === 'active' ? 'Active' : 'Passive'}, ${host.badge.label}`)
            .join('\n'),
    };
}
